// @file Library connections store
import { trackEvent } from '@@/bits/analytics'
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import {
  GoogleAppLicensingSettings as GoogleAppLicensingAPI,
  GoogleClassroom,
  GoogleWorkspace,
  LibraryInfo as LibraryInfoApi,
  LibraryLti as LibraryLtiApi,
  LibraryOauthSettings as LibraryOauthMethodAPI,
  MicrosoftTeams,
} from '@@/dashboard/padlet_api'
import { ApiErrorCode, HttpCode, SnackbarNotificationType } from '@@/enums'
import { useDashboardSettingsStore } from '@@/pinia/dashboard_settings'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSettingsNavigationStore } from '@@/pinia/settings_navigation'
import type {
  GoogleAppLicensingCreateResponse,
  GoogleAppLicensingSettingsResponse,
  GoogleClassroomCourse,
  LibraryOauthMethod,
  LibraryOauthSettings,
  LinkedMicrosoftTenantInfo,
} from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum LibraryConnectionsStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

enum GenerateLinkStatus {
  Initial,
  Loading,
  Completed,
}

enum ToggleSaveStatus {
  Loading = 'Loading',
  Completed = 'Completed',
}

enum DistrictSchoolLmsAccessStatus {
  Loading = 'Loading',
  Completed = 'Completed',
}

const useSettingsLibraryConnectionsStore = defineStore('settingsLibraryConnections', () => {
  // #region: Store Dependencies & Core
  const globalSnackbarStore = useGlobalSnackbarStore()
  const settingsNavigationStore = useSettingsNavigationStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const dashboardSettingsStore = useDashboardSettingsStore()

  const libraryConnectionsStatus = ref<LibraryConnectionsStatus>(LibraryConnectionsStatus.Loading)

  const libraryId = computed((): number | null => settingsNavigationStore.currentLibraryId)
  const libraryName = computed((): string | undefined => settingsNavigationStore.currentLibrary?.name)
  const isLoadingLibraryConnections = computed(
    (): boolean => libraryConnectionsStatus.value === LibraryConnectionsStatus.Loading,
  )

  async function initialize(): Promise<void> {
    if (libraryId.value === null) {
      captureException(new Error('Library ID is null'), {
        context: { source: 'LibraryConnectionsSettingsInitializeNullLibraryId' },
      })
      void globalSnackbarStore.setSnackbar({
        message: __('Error loading page'),
        notificationType: SnackbarNotificationType.error,
      })
      libraryConnectionsStatus.value = LibraryConnectionsStatus.Errored
      return
    }

    libraryConnectionsStatus.value = LibraryConnectionsStatus.Loading

    const promises: Promise<void>[] = [
      fetchLibraryGoogleAppLicensing(libraryId.value),
      fetchLibraryConnectionsInfo(libraryId.value),
    ]

    if (isAppUsing('schoolLibrarySSO')) {
      promises.push(fetchLibraryOauthSettings(libraryId.value))
    }

    try {
      await Promise.all(promises)
      libraryConnectionsStatus.value = LibraryConnectionsStatus.Completed
    } catch (e) {
      captureFetchException(e, { source: 'SettingsLibraryConnectionsInitialize' })
      libraryConnectionsStatus.value = LibraryConnectionsStatus.Errored
    }
  }

  async function fetchLibraryConnectionsInfo(libraryId: number): Promise<void> {
    try {
      const response = await LibraryInfoApi.fetch({ libraryId: libraryId })
      const data = (response.data as JsonAPIResource<any>).attributes

      ltiEnabled.value = data.ltiEnabled ?? false
      ltiUserGroupSyncEnabled.value = data.ltiUserGroupSyncEnabled ?? false
      ltiDefaultGradebookEnabled.value = data.ltiDefaultGradebookEnabled ?? true
      linkedMicrosoftTenantInfo.value = data.linkedMicrosoftTenantInfo ?? {
        microsoftTenantId: null,
        adminConsentGranted: false,
      }
      lmsUserGroupSyncConfigEnabled.value = data.lmsUserGroupSyncConfigEnabled ?? false
      districtSchoolLmsAccessEnabled.value = data.districtSchoolLmsAccessEnabled ?? false
      districtSchoolLmsName.value = data.districtSchoolLmsName ?? 'LMS'
      districtSchoolLmsAccessConfigEnabled.value = data.districtSchoolLmsAccessConfigEnabled ?? false
      googleClassroomEmail.value = data.linkedGoogleClassroomEmail ?? null
      googleWorkspaceEmail.value = data.linkedGoogleWorkspaceEmail ?? null
      googleClassroomRosteringEnabled.value = data.googleClassroomRosteringEnabled ?? false

      libraryConnectionsStatus.value = LibraryConnectionsStatus.Completed

      if (googleClassroomEmail.value) {
        fetchGoogleClassroomCourses()
      }
    } catch (e) {
      captureFetchException(e, { source: 'SettingsLibraryConnectionsFetchLibraryConnectionsInfo' })
      libraryConnectionsStatus.value = LibraryConnectionsStatus.Errored
    }
  }
  // #endregion

  // #region: OAuth Settings
  const libraryOauthSettings = ref<LibraryOauthSettings | null>(null)

  const libraryOauthMethods = computed((): LibraryOauthMethod[] | undefined => libraryOauthSettings.value?.oauth)

  async function fetchLibraryOauthSettings(libraryId: number): Promise<void> {
    try {
      const response = await LibraryOauthMethodAPI.fetch({ libraryId })
      const oauthSettings = (response.data as JsonAPIResource<LibraryOauthSettings>).attributes
      libraryOauthSettings.value = oauthSettings
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryConnectionsFetchLibraryOauthMethods' })
      void globalSnackbarStore.genericFetchError()
      throw new Error(`Failed to fetch library OAuth settings: ${e instanceof Error ? e.message : String(e)}`)
    }
  }
  // #endregion

  // #region: Google Workspace
  const googleWorkspaceEmail = ref<string | null>(null)

  function parseGoogleWorkspaceError(error: any): string {
    let errorMessage = __('Error setting Google Workspace email')

    if (error.status === HttpCode.Unauthorized || error.status === HttpCode.Forbidden) {
      try {
        const parsedErrorMessage = JSON.parse(error.message)
        const apiError = parsedErrorMessage.errors?.[0]

        if (apiError?.code === ApiErrorCode.GOOGLE_WORKSPACE_NOT_ADMIN) {
          errorMessage = __('Only Google Workspace admins can link their account for rostering')
        }
      } catch (jsonException) {
        captureException(jsonException, {
          context: { source: 'SettingsLibraryConnectionsParseGoogleWorkspaceError', originalError: error },
        })
      }
    }

    return errorMessage
  }

  async function setGoogleWorkspaceEmail(email: string): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }

    try {
      await GoogleWorkspace.updateLibraryAdminEmail({
        libraryId: libraryId.value,
        email,
      })

      if (email) {
        googleWorkspaceEmail.value = email
        trackEvent('Library Connections', 'Linked Google Workspace', email, null, {
          libraryId: libraryId.value.toString(),
        })
      }
    } catch (e) {
      const errorMessage = parseGoogleWorkspaceError(e)

      globalSnackbarStore.setSnackbar({
        message: errorMessage,
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'SettingsLibraryConnectionsSetGoogleWorkspaceEmail' })
    }
  }

  async function clearGoogleWorkspaceEmail(): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }
    try {
      await GoogleWorkspace.removeLibraryAdminEmail({
        libraryId: libraryId.value,
      })
      const previousEmail = googleWorkspaceEmail.value
      googleWorkspaceEmail.value = null
      trackEvent('Library Connections', 'Unlinked Google Workspace', previousEmail ?? undefined, null, {
        libraryId: libraryId.value.toString(),
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SettingsLibraryConnectionsClearGoogleWorkspaceEmail' })
    }
  }

  // #endregion

  // #region: Google Classroom Rostering
  const googleClassroomRosteringEnabled = ref<boolean>(false)
  const googleClassroomRosteringEnabledStatus = ref<ToggleSaveStatus>(ToggleSaveStatus.Completed)

  async function setGoogleClassroomRosteringEnabled(value: boolean): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.setSnackbar({
        message: __('Error updating Google Classroom rostering'),
        notificationType: SnackbarNotificationType.error,
      })
      captureException(new Error('Library ID is null'), {
        context: { source: 'SettingsLibraryConnectionsSetGoogleClassroomRostering' },
      })
      return
    }

    try {
      googleClassroomRosteringEnabledStatus.value = ToggleSaveStatus.Loading

      await GoogleWorkspace.updateClassroomRostering({ libraryId: libraryId.value, enabled: value })
      googleClassroomRosteringEnabled.value = value

      trackEvent(
        'Library Connections',
        value ? 'Enabled Google Classroom rostering' : 'Disabled Google Classroom rostering',
        null,
        null,
        {
          libraryId: libraryId.value.toString(),
        },
      )
      globalSnackbarStore.setSnackbar({
        message: value ? __('Google Classroom rostering enabled') : __('Google Classroom rostering disabled'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        message: __('Error updating Google Classroom rostering'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'SettingsLibraryConnectionsSetGoogleClassroomRostering' })
    } finally {
      googleClassroomRosteringEnabledStatus.value = ToggleSaveStatus.Completed
    }
  }
  // #endregion

  // #region: Google App Licensing
  const googleAppLicenseData = ref<GoogleAppLicensingCreateResponse>({ orderId: '', redeemUri: '' })
  const isLibraryUsingGoogleAppLicensing = ref<boolean>(false)
  const isGoogleAppLicenseLoading = ref<boolean>(false)

  async function fetchLibraryGoogleAppLicensing(libraryId: number): Promise<void> {
    try {
      const response = await LibraryOauthMethodAPI.fetchGoogleAppLicensing({ libraryId })
      const googleAppLicensingSettings = (response.data as JsonAPIResource<GoogleAppLicensingSettingsResponse>)
        .attributes
      isLibraryUsingGoogleAppLicensing.value = googleAppLicensingSettings.enabled
      googleAppLicenseData.value.orderId = googleAppLicensingSettings.orderId ?? ''
      googleAppLicenseData.value.redeemUri = googleAppLicensingSettings.redeemUri ?? ''
    } catch (e) {
      captureFetchException(e, { source: 'DashboardSettingsLibraryConnectionsFetchGoogleAppLicensing' })
      void globalSnackbarStore.genericFetchError()
      throw new Error(`Failed to fetch Google App Licensing settings: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  function handleGoogleAppLicensingError(errorMessage: string, intendedState: boolean): void {
    isGoogleAppLicenseLoading.value = false
    // Revert to the opposite of the intended state when an error occurs
    isLibraryUsingGoogleAppLicensing.value = !intendedState
    globalSnackbarStore.setSnackbar({
      message: errorMessage,
      notificationType: SnackbarNotificationType.error,
      timeout: 1500,
    })
  }

  function showConfirmationDialogueGoogleAppLicensing(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      title: __('Enable Google App Licensing?'),
      body: __(
        'Users in %{libraryName} will be signed out and will not be able to sign back in until you turn Google App Licensing off or assign them a license in the Google admin panel',
        { libraryName: libraryName.value },
      ),
      confirmButtonText: __('Confirm'),
      afterConfirmActions: [async () => await updateAllowGoogleAppLicensing(true)],
    })
  }

  async function updateAllowGoogleAppLicensing(useGoogleAppLicensing: boolean): Promise<void> {
    if (libraryId.value === null) {
      captureException(new Error('Library ID is null'), {
        context: { source: 'LibraryConnectionsSettingsUpdateAllowGoogleAppLicensingNullLibraryId' },
      })
      void globalSnackbarStore.setSnackbar({
        message: __('Error updating library login method'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    isLibraryUsingGoogleAppLicensing.value = useGoogleAppLicensing
    isGoogleAppLicenseLoading.value = true

    try {
      if (
        !useGoogleAppLicensing ||
        (googleAppLicenseData.value.orderId !== '' && googleAppLicenseData.value.redeemUri !== '')
      ) {
        await LibraryOauthMethodAPI.updateGoogleAppLicensing({
          libraryId: libraryId.value,
          enable: useGoogleAppLicensing,
          orderId: googleAppLicenseData.value.orderId,
          redeemUri: googleAppLicenseData.value.redeemUri,
        })
      } else {
        const licenseCreationRes = await GoogleAppLicensingAPI.createLicenses(libraryId.value)
        const licenseData = (licenseCreationRes.data as JsonAPIResource<GoogleAppLicensingCreateResponse>).attributes

        await LibraryOauthMethodAPI.updateGoogleAppLicensing({
          libraryId: libraryId.value,
          enable: useGoogleAppLicensing,
          orderId: licenseData.orderId ?? '',
          redeemUri: licenseData.redeemUri ?? '',
        })
        googleAppLicenseData.value = licenseData
      }
      trackEvent(
        'Library Connections',
        useGoogleAppLicensing ? 'Enabled Google App Licensing' : 'Disabled Google App Licensing',
        null,
        null,
        {
          libraryId: libraryId.value.toString(),
        },
      )
      void dashboardSettingsStore.fetchViewableLibraries()
    } catch (e) {
      handleGoogleAppLicensingError(__('Error setting up app licensing. Please try again.'), useGoogleAppLicensing)
      captureFetchException(e, { source: 'LibraryConnectionsSettingsUpdateAllowGoogleAppLicensing' })
    } finally {
      isGoogleAppLicenseLoading.value = false
    }
  }
  // #endregion

  // #region: LTI (Learning Tools Interoperability)
  const ltiDynamicRegistrationLink = ref<string>('')
  const ltiDynamicRegistrationLinkStatus = ref<GenerateLinkStatus>(GenerateLinkStatus.Initial)
  const ltiLinkDeploymentToken = ref<string>('')
  const ltiLinkDeploymentTokenStatus = ref<GenerateLinkStatus>(GenerateLinkStatus.Initial)
  const ltiEnabled = ref<boolean>(false)
  const lmsUserGroupSyncConfigEnabled = ref<boolean>(false)
  const ltiUserGroupSyncEnabled = ref<boolean>(false)
  const ltiUserGroupSyncEnabledStatus = ref<ToggleSaveStatus>(ToggleSaveStatus.Completed)
  const ltiDefaultGradebookEnabled = ref<boolean>(true)
  const ltiDefaultGradebookEnabledStatus = ref<ToggleSaveStatus>(ToggleSaveStatus.Completed)
  const districtSchoolLmsAccessEnabled = ref<boolean>(false)
  const districtSchoolLmsName = ref<string>('')
  const districtSchoolLmsAccessEnabledStatus = ref<DistrictSchoolLmsAccessStatus>(
    DistrictSchoolLmsAccessStatus.Completed,
  )
  const districtSchoolLmsAccessConfigEnabled = ref<boolean>(false)

  async function generateLtiDynamicRegistrationLink(): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }

    try {
      ltiDynamicRegistrationLinkStatus.value = GenerateLinkStatus.Loading

      const response = await LibraryLtiApi.generateLtiDynamicRegistrationUrl(libraryId.value)
      ltiDynamicRegistrationLink.value = response.url
      ltiDynamicRegistrationLinkStatus.value = GenerateLinkStatus.Completed

      trackEvent('Library Connections', 'Generated LTI dynamic registration link', null, null, {
        libraryId: libraryId.value.toString(),
      })
      globalSnackbarStore.setSnackbar({
        message: __('LTI link generated'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      ltiDynamicRegistrationLinkStatus.value = GenerateLinkStatus.Initial

      globalSnackbarStore.setSnackbar({
        message: __('Error generating LTI link'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryConnectionsGenerateLtiAdvantageDynamicRegistrationLink' })
    }
  }

  async function generateLtiLinkDeploymentToken(): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }

    try {
      ltiLinkDeploymentTokenStatus.value = GenerateLinkStatus.Loading

      const response = await LibraryLtiApi.generateLtiLinkDeploymentToken(libraryId.value)
      ltiLinkDeploymentToken.value = response.token
      ltiLinkDeploymentTokenStatus.value = GenerateLinkStatus.Completed

      trackEvent('Library Connections', 'Generated LTI link deployment token', null, null, {
        libraryId: libraryId.value.toString(),
      })
      globalSnackbarStore.setSnackbar({
        message: __('LTI token generated'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      ltiLinkDeploymentTokenStatus.value = GenerateLinkStatus.Initial

      globalSnackbarStore.setSnackbar({
        message: __('Error generating LTI link token'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryConnectionsGenerateLtiLinkDeploymentToken' })
    }
  }

  async function setLtiUserGroupSyncEnabled(value: boolean): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }

    try {
      ltiUserGroupSyncEnabledStatus.value = ToggleSaveStatus.Loading

      await LibraryLtiApi.updateLtiUserGroupSyncEnabled(libraryId.value, value)
      ltiUserGroupSyncEnabled.value = value

      trackEvent(
        'Library Connections',
        value ? 'Enabled LTI User Group Sync' : 'Disabled LTI User Group Sync',
        null,
        null,
        {
          libraryId: libraryId.value.toString(),
        },
      )
      globalSnackbarStore.setSnackbar({
        message: value ? __('User Group Sync enabled') : __('User Group Sync disabled'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'LibraryConnectionsUserGroupSync' })
    } finally {
      ltiUserGroupSyncEnabledStatus.value = ToggleSaveStatus.Completed
    }
  }

  async function setLtiDefaultGradebookEnabled(value: boolean): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }

    try {
      ltiDefaultGradebookEnabledStatus.value = ToggleSaveStatus.Loading

      await LibraryLtiApi.updateLtiDefaultGradebookEnabled(libraryId.value, value)
      ltiDefaultGradebookEnabled.value = value

      globalSnackbarStore.setSnackbar({
        message: value ? __('Gradebook enabled by default') : __('Gradebook disabled by default'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'LibraryConnectionsDefaultGradebook' })
    } finally {
      ltiDefaultGradebookEnabledStatus.value = ToggleSaveStatus.Completed
    }
  }

  async function setDistrictSchoolLmsAccessEnabled(value: boolean): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }

    try {
      districtSchoolLmsAccessEnabledStatus.value = DistrictSchoolLmsAccessStatus.Loading
      districtSchoolLmsAccessEnabled.value = value
      await LibraryLtiApi.updateDistrictSchoolLmsAccessEnabled(libraryId.value, value)

      trackEvent(
        'Library Connections',
        value ? 'Enabled District School LMS Access' : 'Disabled District School LMS Access',
        districtSchoolLmsName.value,
        null,
        {
          libraryId: libraryId.value.toString(),
        },
      )
      globalSnackbarStore.setSnackbar({
        message: value
          ? __('%{lmsName} access enabled', { lmsName: districtSchoolLmsName.value })
          : __('%{lmsName} access disabled', { lmsName: districtSchoolLmsName.value }),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      districtSchoolLmsAccessEnabled.value = !value
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'LibraryConnectionsDistrictSchoolLmsAccess' })
    } finally {
      districtSchoolLmsAccessEnabledStatus.value = DistrictSchoolLmsAccessStatus.Completed
    }
  }
  // #endregion

  // #region: Google Classroom
  const googleClassroomDeploymentToken = ref<string>('')
  const googleClassroomDeploymentTokenStatus = ref<GenerateLinkStatus>(GenerateLinkStatus.Initial)
  const googleClassroomEmail = ref<string | null>(null)
  const googleClassroomCourses = ref<GoogleClassroomCourse[]>([])
  const fetchingGoogleClassroomCourses = ref<boolean>(false)

  function parseGoogleClassroomError(error: any, defaultMessage: string): string {
    if (error.status === HttpCode.Unauthorized || error.status === HttpCode.Forbidden) {
      try {
        const parsedErrorMessage = JSON.parse(error.message)
        const apiError = parsedErrorMessage.errors?.[0]

        if (apiError?.code === ApiErrorCode.GOOGLE_CLASSROOM_LOGIN_REQUIRED) {
          return __('Your Google account connection has expired. Please reconnect your Google account.')
        }
        if (apiError?.code === ApiErrorCode.GOOGLE_CLASSROOM_NOT_ADMIN) {
          return __('Only workspace admins or owners can manage Google Classroom settings.')
        }
      } catch (jsonException) {
        captureException(jsonException, {
          context: { source: 'SettingsLibraryConnectionsParseGoogleClassroomError', originalError: error },
        })
      }
    }

    return defaultMessage
  }

  async function generateGoogleClassroomDeploymentToken(): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      captureException(new Error('LibraryId is null in generateGoogleClassroomDeploymentToken'))
      return
    }

    try {
      googleClassroomDeploymentTokenStatus.value = GenerateLinkStatus.Loading

      const response = await LibraryLtiApi.generateGoogleClassroomDeploymentToken(libraryId.value)
      googleClassroomDeploymentToken.value = response.token
      googleClassroomDeploymentTokenStatus.value = GenerateLinkStatus.Completed

      trackEvent('Library Connections', 'Generated Google Classroom deployment token', null, null, {
        libraryId: libraryId.value.toString(),
      })
      globalSnackbarStore.setSnackbar({
        message: __('Google Classroom deployment token generated'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      googleClassroomDeploymentTokenStatus.value = GenerateLinkStatus.Initial

      globalSnackbarStore.setSnackbar({
        message: __('Error generating Google Classroom deployment token'),
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LibraryConnectionsGenerateGoogleClassroomDeploymentToken' })
    }
  }

  async function setGoogleClassroomEmail(email: string): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }
    try {
      await GoogleClassroom.updateLibraryAdminEmail({
        libraryId: libraryId.value,
        email,
      })
    } catch (e) {
      const errorMessage = parseGoogleClassroomError(e, __('Error setting Google Classroom email'))
      globalSnackbarStore.setSnackbar({
        message: errorMessage,
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'SettingsLibraryConnectionsSetGoogleClassroomEmail' })
      return
    }

    if (email) {
      googleClassroomEmail.value = email
      trackEvent('Library Connections', 'Linked Google Classroom', email, null, {
        libraryId: libraryId.value.toString(),
      })
      fetchGoogleClassroomCourses(true)
    }
  }

  async function clearGoogleClassroomEmail(): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }
    try {
      await GoogleClassroom.removeLibraryAdminEmail({
        libraryId: libraryId.value,
      })
      const previousEmail = googleClassroomEmail.value
      googleClassroomEmail.value = null
      googleClassroomCourses.value = []
      trackEvent('Library Connections', 'Unlinked Google Classroom', previousEmail ?? undefined, null, {
        libraryId: libraryId.value.toString(),
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SettingsLibraryConnectionsClearGoogleClassroomEmail' })
    }
  }

  async function fetchGoogleClassroomCourses(fromRemote: boolean = false): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }
    try {
      fetchingGoogleClassroomCourses.value = true
      const response = await GoogleClassroom.admin_courses({ libraryId: libraryId.value, fromRemote })
      const data = response.data as JsonAPIResource<GoogleClassroomCourse[]>
      googleClassroomCourses.value = data.attributes ?? []
    } catch (e) {
      const errorMessage = parseGoogleClassroomError(e, __('Error fetching Google Classroom courses'))
      captureFetchException(e, { source: 'SettingsLibraryConnectionsFetchGoogleClassroomCourses' })
      globalSnackbarStore.setSnackbar({
        message: errorMessage,
        notificationType: SnackbarNotificationType.error,
      })
    } finally {
      fetchingGoogleClassroomCourses.value = false
    }
  }
  // #endregion

  // #region: Microsoft Teams
  const linkedMicrosoftTenantInfo = ref<LinkedMicrosoftTenantInfo>({
    microsoftTenantId: null,
    adminConsentGranted: false,
  })
  const microsoftConnectStatus = ref<GenerateLinkStatus>(GenerateLinkStatus.Initial)

  async function handleMicrosoftTeamsLink(): Promise<void> {
    // Construct the OAuth URL directly based on the current environment
    const baseUrl = `${window.location.protocol}//${window.location.host}`
    const url = `${baseUrl}/auth/ms_graph?library_id=${libraryId.value}&scope_alias=microsoft_teams&afterwards=check_microsoft_teams_emails`
    const popup = window.open(url, 'ms_teams_link_library', 'width=800,height=600')

    let completed = false

    const cleanup = (): void => {
      window.removeEventListener('microsoft_teams_auth', onCustomEvent as any)
      window.removeEventListener('message', onMessage as any)
      clearInterval(interval)
    }

    const onSuccess = async (microsoftTenantId?: string): Promise<void> => {
      if (completed) return
      completed = true
      cleanup()

      // If we received the tenant ID directly from the event, update the state immediately
      if (microsoftTenantId) {
        linkedMicrosoftTenantInfo.value = {
          ...linkedMicrosoftTenantInfo.value,
          microsoftTenantId,
        }
        trackEvent('Library Connections', 'Linked Microsoft Teams', microsoftTenantId, null, {
          libraryId: libraryId.value?.toString(),
        })
        globalSnackbarStore.setSnackbar({
          message: __('Microsoft connected successfully.'),
          notificationType: SnackbarNotificationType.success,
        })
      }
    }

    const onCustomEvent = async (e: Event): Promise<void> => {
      const customEvent = e as CustomEvent
      if (customEvent.type === 'microsoft_teams_auth') {
        const microsoftTenantId = customEvent.detail?.microsoftTenantId
        await onSuccess(microsoftTenantId)
        return
      }
      if (customEvent.type === 'microsoft_teams_auth_error') {
        cleanup()
        const errorCode = customEvent.detail?.code
        const closeButtonText = errorCode === 'mismatched_microsoft_email' ? __('Switch email account') : __('OK')
        globalAlertDialogStore.openAlertDialog({
          iconSrc: ALERT_ICON.iconSrc,
          title: __('Could not link to Microsoft Teams'),
          htmlText: customEvent.detail?.message,
          closeButtonText,
        })
        microsoftConnectStatus.value = GenerateLinkStatus.Initial
      }
    }

    const onMessage = async (event: MessageEvent): Promise<void> => {
      // Only accept messages from our own origin (dev: padlet-dev.ngrok.io, prod: padlet.com)
      if (event.origin !== window.location.origin) return
      if (event?.data?.type === 'padlet:microsoft_teams_auth' && event?.data?.success) {
        const microsoftTenantId = event.data?.microsoftTenantId
        await onSuccess(microsoftTenantId)
      }
      if (event?.data?.type === 'microsoft_teams_auth_error') {
        cleanup()
        globalAlertDialogStore.openAlertDialog({
          iconSrc: ALERT_ICON.iconSrc,
          title: __('Could not link to Microsoft Teams'),
          htmlText: event.data?.message,
          closeButtonText: __('OK'),
        })
        microsoftConnectStatus.value = GenerateLinkStatus.Initial
      }
    }

    window.addEventListener('microsoft_teams_auth', onCustomEvent as any)
    window.addEventListener('microsoft_teams_auth_error', onCustomEvent as any)
    window.addEventListener('message', onMessage as any)

    const interval = setInterval(() => {
      void (async () => {
        if (popup === null || popup.closed) {
          cleanup()
          if (!completed) {
            const maxTries = 120
            for (let i = 0; i < maxTries; i++) {
              if (linkedMicrosoftTenantInfo.value.adminConsentGranted) break
              await new Promise((resolve) => setTimeout(resolve, 500))
            }
          }
        }
      })()
    }, 400)
  }

  async function handleMicrosoftTeamsUnlink(): Promise<void> {
    if (libraryId.value === null) {
      globalSnackbarStore.genericFetchError()
      return
    }
    try {
      microsoftConnectStatus.value = GenerateLinkStatus.Loading
      const previousTenantId = linkedMicrosoftTenantInfo.value.microsoftTenantId
      await MicrosoftTeams.unlink({ libraryId: libraryId.value })
      linkedMicrosoftTenantInfo.value = {
        microsoftTenantId: null,
        adminConsentGranted: false,
      }
      trackEvent('Library Connections', 'Unlinked Microsoft Teams', previousTenantId ?? undefined, null, {
        libraryId: libraryId.value.toString(),
      })
      globalSnackbarStore.setSnackbar({
        message: __('Microsoft disconnected.'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SettingsLibraryConnectionsHandleMicrosoftTeamsUnlink' })
    } finally {
      microsoftConnectStatus.value = GenerateLinkStatus.Initial
    }
  }
  // #endregion

  return {
    // #region: Core
    libraryId,
    libraryName,
    isLoadingLibraryConnections,
    initialize,
    fetchLibraryConnectionsInfo,
    // #endregion

    // #region: OAuth Settings
    libraryOauthMethods,
    fetchLibraryOauthSettings,
    // #endregion

    // #region: Google Workspace
    googleWorkspaceEmail,
    setGoogleWorkspaceEmail,
    clearGoogleWorkspaceEmail,
    // #endregion

    // #region: Google Classroom Rostering
    googleClassroomRosteringEnabled,
    googleClassroomRosteringEnabledStatus,
    setGoogleClassroomRosteringEnabled,
    // #endregion

    // #region: Google App Licensing
    isLibraryUsingGoogleAppLicensing,
    isGoogleAppLicenseLoading,
    googleAppLicenseData,
    fetchLibraryGoogleAppLicensing,
    updateAllowGoogleAppLicensing,
    showConfirmationDialogueGoogleAppLicensing,
    handleGoogleAppLicensingError,
    // #endregion

    // #region: LTI (Learning Tools Interoperability)
    ltiDynamicRegistrationLink,
    ltiDynamicRegistrationLinkStatus,
    ltiLinkDeploymentToken,
    ltiLinkDeploymentTokenStatus,
    ltiEnabled,
    lmsUserGroupSyncConfigEnabled,
    ltiUserGroupSyncEnabled,
    ltiUserGroupSyncEnabledStatus,
    ltiDefaultGradebookEnabled,
    ltiDefaultGradebookEnabledStatus,
    districtSchoolLmsAccessEnabled,
    districtSchoolLmsName,
    districtSchoolLmsAccessEnabledStatus,
    districtSchoolLmsAccessConfigEnabled,
    generateLtiDynamicRegistrationLink,
    generateLtiLinkDeploymentToken,
    setLtiUserGroupSyncEnabled,
    setLtiDefaultGradebookEnabled,
    setDistrictSchoolLmsAccessEnabled,
    // #endregion

    // #region: Google Classroom
    googleClassroomDeploymentToken,
    googleClassroomDeploymentTokenStatus,
    googleClassroomEmail,
    googleClassroomCourses,
    fetchingGoogleClassroomCourses,
    generateGoogleClassroomDeploymentToken,
    setGoogleClassroomEmail,
    clearGoogleClassroomEmail,
    fetchGoogleClassroomCourses,
    // #endregion

    // #region: Microsoft Teams
    linkedMicrosoftTenantInfo,
    microsoftConnectStatus,
    handleMicrosoftTeamsLink,
    handleMicrosoftTeamsUnlink,
    // #endregion
  }
})
export { DistrictSchoolLmsAccessStatus, GenerateLinkStatus, ToggleSaveStatus, useSettingsLibraryConnectionsStore }
